import React from 'react';
import PropTypes from 'prop-types';
import { Box, Grid } from '@mantine/core';
import EditDrawerFieldRenderer from './EditDrawerFieldRenderer';
import { PosSyncLockedField } from './PosSyncLockedField';
import { isPosSyncEditableField } from './helpers/posSyncEditableFields';

export default function EditDrawerForm({
  record,
  form,
  fieldRendererProps,
  editColumns,
  isPosSyncLocked = false,
}) {
  /* On a POS sync-in-place menu only the merchant-owned fields stay editable (CN-50652);
  everything else renders as-is but locked, with a hover notice pointing at the POS. */
  const withPosSyncLock = (column, field) => {
    if (!isPosSyncLocked) return field;
    if (isPosSyncEditableField({ itemType: record?.itemType, dataIndex: column?.dataIndex })) {
      return field;
    }
    return <PosSyncLockedField>{field}</PosSyncLockedField>;
  };

  return (
    <form form={form}>
      {editColumns.map(column => {
        if (!column || (column?.dataIndex === 'internalName' && record?.externalId)) {
          return null;
        }

        if (column?.dataIndex === 'imageUrl') {
          return null;
        }

        // checks if this is a min/max field (like min and max in modifier groups)
        // if it is, it renders two fields side by side
        if (column?.dataIndex === 'minMax') {
          return (
            <Box key={`${column.dataIndex}-${record.key}`} mb="lg">
              {withPosSyncLock(
                column,
                <Grid gutter="xs">
                  <Grid.Col span={6}>
                    <EditDrawerFieldRenderer
                      column={column.minimum}
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...fieldRendererProps}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <EditDrawerFieldRenderer
                      column={column.maximum}
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...fieldRendererProps}
                    />
                  </Grid.Col>
                </Grid>,
              )}
            </Box>
          );
        }

        const marginBottom = column?.dataIndex === 'allowMultipleModifiers' ? 'xs' : 'lg';

        return (
          <Box key={`${column.dataIndex || column.key}-${record.key}`} mb={marginBottom}>
            {withPosSyncLock(
              column,
              <EditDrawerFieldRenderer
                column={column}
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...fieldRendererProps}
              />,
            )}
          </Box>
        );
      })}
    </form>
  );
}

EditDrawerForm.propTypes = {
  record: PropTypes.shape({
    key: PropTypes.string,
    externalId: PropTypes.string,
    itemType: PropTypes.string,
  }).isRequired,
  form: PropTypes.shape(),
  fieldRendererProps: PropTypes.shape(),
  editColumns: PropTypes.arrayOf(PropTypes.shape()),
  isPosSyncLocked: PropTypes.bool,
};
